import { useCallback, useState } from 'react';

import { XemploInputProps } from './xemplo-input.types';

/**
 * Tracks whether the input has content, which drives the `has-value` class and so the
 * floating label's position.
 *
 * `hasValue` is derived during render rather than mirrored into state by an effect. An
 * effect commits a frame after the value it is following, so every transient value
 * change was painted twice — once with the stale class — which is enough for a
 * transitioned label to visibly move. Uncontrolled inputs have no `value` to derive
 * from, so those still track it from `onChange`.
 */
export function useValueTracker({ value, onChange }: XemploInputProps) {
  const [uncontrolledHasValue, setUncontrolledHasValue] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUncontrolledHasValue(!!e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  const hasValue = value == null ? uncontrolledHasValue : !!value;

  return { hasValue, handleChange };
}
